/**
 * This browser's side of notifications: whether it can show them, what its person said, and its
 * sign-up with the browser's push service (through our service worker, /sw.js).
 */

export type DevicePermission = NotificationPermission | 'unsupported';

/** An iPhone or iPad (iPadOS reports itself as a Mac, but has touch). */
export const appleMobileDevice = (): 'iPhone' | 'iPad' | null =>
  /iPhone|iPod/.test(navigator.userAgent)
    ? 'iPhone'
    : /iPad/.test(navigator.userAgent) ||
        (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
      ? 'iPad'
      : null;

/** Opened from the Home Screen as an app, not in a browser tab. */
export const openedAsApp = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const pushSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const devicePermission = (): DevicePermission =>
  pushSupported() ? Notification.permission : 'unsupported';

/** A base64url key as the bytes `pushManager.subscribe` wants. */
const keyBytes = (base64url: string): Uint8Array<ArrayBuffer> => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const sameKey = (key: ArrayBuffer | null, expected: Uint8Array): boolean =>
  key !== null &&
  key.byteLength === expected.length &&
  new Uint8Array(key).every((byte, i) => byte === expected[i]);

/** This browser's sign-up, if it has one. */
export const thisDeviceSubscription = async (): Promise<PushSubscription | null> => {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return registration ? registration.pushManager.getSubscription() : null;
};

/** Sign this browser up for our key (re-signing if it was signed up for another), and return
 *  what the server needs to reach it. Needs permission already granted. */
export const signUpThisDevice = async (publicKey: string): Promise<PushSubscriptionJSON> => {
  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const key = keyBytes(publicKey);
  const existing = await registration.pushManager.getSubscription();
  if (existing && sameKey(existing.options.applicationServerKey, key)) return existing.toJSON();
  await existing?.unsubscribe();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  return subscription.toJSON();
};

/** Take this browser off; returns the address the server should forget, if it had one. */
export const signOutThisDevice = async (): Promise<string | null> => {
  const subscription = await thisDeviceSubscription();
  if (!subscription) return null;
  await subscription.unsubscribe();
  return subscription.endpoint;
};
